<template>
	<section>
		<header class="heading">
			<h2>{{ t("contactResearchPage.result.background") }}</h2>
			<span>{{ t("contactResearchPage.result.backgroundDescription") }}</span>
		</header>
		<AccordionRoot multiple class="accordions" persistence-key="contact-research-result-accordion">
			<AccordionItem
				v-for="progress in providerProgress"
				:key="progress.provider.id"
				:value="progress.provider.id"
				class="accordion"
			>
				<template #title>{{ progress.provider.label }}</template>
				<template #header-actions>
					<span class="provider-meta">
						<span class="status-badge" :class="progress.status">{{ statusLabel(progress) }}</span>
						<span>{{ formatProviderDuration(progress) }}</span>
					</span>
				</template>
				<div class="body">
					<template v-if="progress.provider.id === ContactResearchProviderId.WebSearch">
						<p v-if="profileBio" class="profile-bio">{{ profileBio }}</p>
						<dl class="source-facts">
							<div v-for="fact in profileFacts" :key="fact.label">
								<dt>{{ fact.label }}</dt>
								<dd>{{ fact.value }}</dd>
							</div>
						</dl>
					</template>
					<p v-if="progress.result?.error || !progress.result?.sanitizedContacts.length" class="source-empty">
						{{ progress.result?.error || t("contactResearchPage.activity.noResult") }}
					</p>
					<template
						v-for="(contact, contactIndex) in progress.result?.sanitizedContacts ?? []"
						:key="contactKey(contact, contactIndex)"
					>
						<dl class="source-facts">
							<div v-for="fact in contactFacts(contact, progress.provider.id)" :key="`${fact.label}-${fact.value}`">
								<dt>{{ fact.label }}</dt>
								<dd>
									<a v-if="fact.href" :href="fact.href" :target="fact.external ? '_blank' : undefined" rel="noreferrer">
										{{ fact.value }}
									</a>
									<span v-else>{{ fact.value }}</span>
								</dd>
							</div>
						</dl>
						<div v-if="contact.sourceUrls?.length" class="pages-read">
							<strong>{{ t("contactResearchPage.result.pagesRead") }}</strong>
							<a
								v-for="source in contact.sourceUrls ?? []"
								:key="source.url"
								:href="source.url"
								target="_blank"
								rel="noreferrer"
							>
								<AppIcon :name="IconName.ExternalLink" :size="13" />{{ sourceLabel(source.url) }}
							</a>
						</div>
					</template>
					<div v-if="progress.result" class="raw-json-action">
						<Button variant="secondary" size="xs" @click="exportProvider(progress)">
							<AppIcon :name="IconName.Download" :size="14" />
							{{ t("contactResearchPage.result.rawJson") }}
						</Button>
					</div>
				</div>
			</AccordionItem>
			<AccordionItem value="inputs" class="accordion">
				<template #title>{{ t("contactResearchPage.result.searchInputs") }}</template>
				<template #header-actions>
					<span class="meta">{{ t("contactResearchPage.result.submitted") }}</span>
				</template>
				<div class="body">
					<dl class="source-facts">
						<div v-for="fact in searchInputFacts" :key="fact.label">
							<dt>{{ fact.label }}</dt>
							<dd>
								<a v-if="fact.href" :href="fact.href" target="_blank" rel="noreferrer">{{ fact.value }}</a>
								<span v-else>{{ fact.value }}</span>
							</dd>
						</div>
					</dl>
				</div>
			</AccordionItem>
		</AccordionRoot>
	</section>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import AccordionItem from "@/components/ui/AccordionItem.vue"
import AccordionRoot from "@/components/ui/AccordionRoot.vue"
import Button from "@/components/ui/Button.vue"
import { IconName } from "@/components/icons/registry"
import {
	ContactResearchProviderId,
	ContactResearchProviderProgressStatus,
	type ContactResearchContact,
	type ContactResearchProviderProgress,
} from "@/types/contactResearch"

type ContactFact = {
	label: string
	value: string
	href?: string
	external?: boolean
}

const props = defineProps<{
	providerProgress: ContactResearchProviderProgress[]
	currentTime: number
	profileBio: string
	profileFacts: ContactFact[]
	searchInputFacts: ContactFact[]
}>()

const { t } = useI18n()

function statusLabel(progress: ContactResearchProviderProgress): string {
	return t(`contactResearchPage.status.${progress.status}`)
}

function formatDuration(durationMs: number): string {
	const seconds = Math.max(0, Math.floor(durationMs / 1000))
	const minutes = Math.floor(seconds / 60)
	const remainingSeconds = seconds % 60

	if (minutes > 0) {
		return `${minutes}m ${remainingSeconds}s`
	}

	return `${seconds}s`
}

function formatProviderDuration(progress: ContactResearchProviderProgress): string {
	if (progress.result) {
		return formatDuration(progress.result.durationMs)
	}

	if (!progress.startedAt || progress.status === ContactResearchProviderProgressStatus.Pending) {
		return formatDuration(0)
	}

	return formatDuration(props.currentTime - new Date(progress.startedAt).getTime())
}

function linkedInHref(value: string): string {
	if (/^https?:\/\//.test(value)) {
		return value
	}

	if (value.startsWith("/")) {
		return `https://www.linkedin.com${value}`
	}

	return `https://${value}`
}

function contactFacts(contact: ContactResearchContact, providerId: ContactResearchProviderId): ContactFact[] {
	const isWebSearch = providerId === ContactResearchProviderId.WebSearch

	return [
		{
			label: t("contactResearchPage.matches.fields.fullName"),
			value: contact.fullName,
		},
		...(contact.companyName && !isWebSearch
			? [
					{
						label: t("contactResearchPage.matches.fields.companyName"),
						value: contact.companyName,
					},
				]
			: []),
		...(contact.jobTitle
			? [
					{
						label: t("contactResearchPage.matches.fields.jobTitle"),
						value: contact.jobTitle,
					},
				]
			: []),
		...(contact.location && !isWebSearch
			? [
					{
						label: t("contactResearchPage.matches.fields.location"),
						value: contact.location,
					},
				]
			: []),
		...(contact.emails ?? []).map(value => ({
			label: t("contactResearchPage.matches.email"),
			value,
			href: `mailto:${value}`,
		})),
		...(contact.phones ?? []).map(value => ({
			label: t("contactResearchPage.matches.phone"),
			value,
			href: `tel:${value}`,
		})),
		...(contact.companyPhones ?? []).map(value => ({
			label: t("contactResearchPage.matches.companyPhone"),
			value,
			href: `tel:${value}`,
		})),
		...(contact.linkedInUrl
			? [
					{
						label: "LinkedIn",
						value: contact.linkedInUrl,
						href: linkedInHref(contact.linkedInUrl),
						external: true,
					},
				]
			: []),
	]
}

function contactKey(contact: ContactResearchContact, index: number): string {
	return `${contact.source}-${contact.fullName}-${contact.linkedInUrl ?? index}`
}

function sourceLabel(sourceUrl: string): string {
	try {
		const url = new URL(sourceUrl)

		return `${url.hostname.replace(/^www\./, "")}${url.pathname === "/" ? "" : url.pathname}`
	} catch {
		return sourceUrl
	}
}

function downloadJson(data: unknown, fileName: string): void {
	const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
	const url = URL.createObjectURL(blob)
	const link = document.createElement("a")
	link.href = url
	link.download = fileName
	link.click()
	URL.revokeObjectURL(url)
}

function exportProvider(progress: ContactResearchProviderProgress): void {
	downloadJson(progress.result, `contact-research-${progress.provider.id}.json`)
}
</script>

<style scoped>
.heading {
	display: flex;
	align-items: baseline;
	gap: var(--space-12);
	margin-bottom: var(--space-12);

	h2,
	span {
		margin: 0;
	}

	h2 {
		font-size: var(--font-size-large);
	}

	span {
		color: var(--neutral-600);
		font-size: var(--font-size-small);
	}
}

.accordions {
	gap: 0;
}

.accordion {
	border-radius: 0;
	box-shadow: none;

	&:first-child {
		border-radius: var(--border-radius-md) var(--border-radius-md) 0 0;
	}

	&:last-child {
		border-radius: 0 0 var(--border-radius-md) var(--border-radius-md);
	}

	& + & {
		border-top: 0;
	}
}

.accordion:deep(.accordion-header) {
	padding-right: var(--space-18);
}

.accordion:deep(.accordion-trigger) {
	padding: var(--space-14) var(--space-18);
}

.accordion:deep(.accordion-legend) {
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-semibold);
}

.accordion:deep(.accordion-content-wrapper .accordion-content) {
	padding: 0;
	border-top: 0;
}

.accordion:deep(.accordion-content-wrapper[data-state="open"]) {
	border-top: var(--border-width-thin) solid var(--neutral-200);
}

.meta,
.provider-meta {
	color: var(--neutral-600);
	font-size: var(--font-size-xs);
}

.provider-meta {
	display: grid;
	grid-template-columns: 1fr var(--space-48);
	align-items: center;
	gap: var(--space-12);
	width: 140px;

	.status-badge,
	> span:last-child {
		justify-self: end;
	}
}

.status-badge {
	display: inline-flex;
	align-items: center;
	width: max-content;
	padding: var(--space-3) var(--space-8);
	color: var(--neutral-700);
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-medium);
	white-space: nowrap;
	background: var(--neutral-100);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-full);

	&.success {
		color: var(--primary-green);
		background: color-mix(in srgb, var(--primary-green) 7%, var(--neutral-0));
		border-color: color-mix(in srgb, var(--primary-green) 35%, var(--neutral-0));
	}

	&.empty,
	&.skipped,
	&.error {
		color: var(--accent-warning);
		background: color-mix(in srgb, var(--accent-warning) 7%, white);
		border-color: color-mix(in srgb, var(--accent-warning) 25%, white);
	}
}

.body {
	display: grid;
	gap: var(--space-16);
	padding: 0 var(--space-40) var(--space-18);

	:deep(.btn) {
		justify-self: start;
		gap: var(--space-6);
	}

	.raw-json-action {
		justify-self: start;
		width: max-content;
	}
}

.profile-bio,
.source-empty {
	max-width: 760px;
	margin: 0;
	color: var(--neutral-700);
	font-size: var(--font-size-small);
	line-height: var(--line-height-relaxed);
}

.source-facts {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 0 var(--space-32);
	margin: 0;

	> div {
		display: flex;
		align-items: baseline;
		justify-content: space-between;
		gap: var(--space-12);
		min-width: 0;
		padding: var(--space-8) 0;
		border-bottom: var(--border-width-thin) solid var(--neutral-200);
	}

	dt,
	dd {
		min-width: 0;
		margin: 0;
		font-size: var(--font-size-small);
	}

	dt {
		color: var(--neutral-600);
	}

	dd {
		overflow: hidden;
		text-align: right;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	a {
		color: var(--accent-info);
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}
}

.pages-read {
	display: grid;
	justify-items: start;
	gap: var(--space-6);

	strong {
		color: var(--neutral-600);
		font-size: var(--font-size-xs);
	}

	a {
		display: inline-flex;
		align-items: center;
		gap: var(--space-6);
		color: var(--accent-info);
		font-size: var(--font-size-small);
		text-decoration: none;

		&:hover {
			text-decoration: underline;
		}
	}
}

@media (max-width: 640px) {
	.heading {
		align-items: flex-start;
		flex-direction: column;
		gap: var(--space-4);
	}

	.accordion:deep(.accordion-header) {
		align-items: flex-start;
		padding-right: var(--space-12);
	}

	.provider-meta {
		gap: var(--space-8);
		padding-top: var(--space-12);
	}

	.body {
		padding-right: var(--space-18);
		padding-left: var(--space-18);
	}

	.source-facts {
		grid-template-columns: 1fr;
	}
}
</style>
